import { useEffect, useState } from "react";
import Auth from "../../../Auth";
import { queryClient } from "../../../lib/query-client";
import { useProjectStore } from "../../../stores/projectStore";

export const useTrashedItems = (showDeleted, projectId, seriesId) => {
  const [trashedCharacters, setTrashedCharacters] = useState([]);
  const [trashedEntries, setTrashedEntries] = useState([]);
  const [trashedChats, setTrashedChats] = useState([]);
  const setTrashedItem = useProjectStore((state) => state.setTrashedItem);
  const setRefreshTrashedItems = useProjectStore((state) => state.setRefreshTrashedItems);

  const restoreChacater = async (characterId) => {
    await Auth.fetchWithIdToken(`/api/characters/${characterId}/restore`, { method: "post" });
    fetchTrashedCharacters();
    setTrashedItem();
  };

  const fetchTrashedCharacters = async () => {
    const { characters } = await Auth.fetchWithIdToken("/api/characters/trashed", { method: "post", projectId, seriesId });
    const mapped = characters.map((character) => ({
      ...character,
      collapsed: false, // trashed items should never be collapsed.
      restore: () => restoreChacater(character.id),
    }));
    setTrashedCharacters(mapped);
  };

  const restoreEntry = async (entryId) => {
    await Auth.fetchWithIdToken(`/api/worldbuildingEntries/${entryId}/restore`, { method: "post" });
    fetchTrashedEntries();
    setTrashedItem();
  };

  const fetchTrashedEntries = async () => {
    const { entries } = await Auth.fetchWithIdToken("/api/worldbuildingEntries/trashed", { method: "post", projectId, seriesId });
    const mapped = entries.map((entry) => ({
      ...entry,
      collapsed: false, // trashed items should never be collapsed.
      restore: () => restoreEntry(entry.id),
    }));
    setTrashedEntries(mapped);
  };

  const restoreChat = async (chatId, onRestored) => {
    await Auth.fetchWithIdToken(`/api/chats/${chatId}/restore`, { method: "post" });
    fetchTrashedChats();
    setTrashedItem();
    // Refetch the chat list query and wait for it to complete so the restored chat is available
    await queryClient.refetchQueries({ queryKey: ["chats", projectId], exact: true });
    if (onRestored) {
      onRestored();
    }
  };

  const fetchTrashedChats = async () => {
    const { chats } = await Auth.fetchWithIdToken("/api/chats/trashed", { method: "post", projectId });
    const mapped = chats.map((chat) => ({
      ...chat,
      restore: (onRestored) => restoreChat(chat.id, onRestored),
    }));
    setTrashedChats(mapped);
  };

  const refresh = async () => {
    await Promise.all([fetchTrashedCharacters(), fetchTrashedEntries(), fetchTrashedChats()]);
  };

  useEffect(() => {
    setRefreshTrashedItems(refresh);
    // showDeleted is included so that we refresh whenever it changes, keeping the trash count mostly accurate.
    if (projectId) {
      (async () => {
        await refresh();
      })().catch(console.error);
    }
  }, [showDeleted, projectId]);

  return { trashedCharacters, trashedEntries, trashedChats };
};
